import React, { useState } from "react";
import { SubscriptionClient } from "../sdk";
import PendingOrders from "./PendingOrder";
import BucketPositions from "./Position";
import BucketCollections from "./BucketCollection";
import "./smartlayout/layout.css";
import "./TabSection.css";

export type TabSectionTabId = "orders" | "positions" | "buckets";

export interface TabSectionProps {
  baseURL: string;
  subscriptionClient: SubscriptionClient;
  authToken?: string;
  walletAddress: string;
  mnemonic?: string;
  height?: string;
  refreshInterval?: number;
  /** Passed to Positions and used when view is "by bucket". */
  selectedBucketId?: string | null;
  /** Called when a bucket is selected in Bucket Collections (e.g. to sync with Positions). */
  onBucketSelect?: (bucketId: string, totalEquity: number) => void;
}

const TAB_LABELS: Record<TabSectionTabId, string> = {
  orders: "Orders",
  positions: "Positions",
  buckets: "Buckets",
};

function TabSection({
  baseURL,
  subscriptionClient,
  authToken,
  walletAddress,
  mnemonic,
  height = "400px",
  refreshInterval,
  selectedBucketId = null,
  onBucketSelect,
}: TabSectionProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabSectionTabId>("orders");

  return (
    <div className="layout-container tab-section-root" style={{ height }}>
      <div className="layout-header tab-section-header">
        <div className="tab-section-tabs">
          {(Object.keys(TAB_LABELS) as TabSectionTabId[]).map((tabId) => (
            <button
              key={tabId}
              type="button"
              className={`btn-tab ${activeTab === tabId ? "active" : ""}`}
              onClick={() => setActiveTab(tabId)}
            >
              {TAB_LABELS[tabId]}
            </button>
          ))}
        </div>
      </div>
      <div className="tab-section-content">
        {activeTab === "orders" && (
          <PendingOrders
            baseURL={baseURL}
            subscriptionClient={subscriptionClient}
            authToken={authToken}
            walletAddress={walletAddress}
            mnemonic={mnemonic}
            height="100%"
            refreshInterval={refreshInterval}
          />
        )}
        {activeTab === "positions" && (
          <BucketPositions
            baseURL={baseURL}
            subscriptionClient={subscriptionClient}
            authToken={authToken}
            walletAddress={walletAddress}
            mnemonic={mnemonic}
            height="100%"
            refreshInterval={refreshInterval}
            selectedBucketId={selectedBucketId}
          />
        )}
        {activeTab === "buckets" && (
          <BucketCollections
            baseURL={baseURL}
            subscriptionClient={subscriptionClient}
            walletAddress={walletAddress}
            mnemonic={mnemonic ?? ""}
            authToken={authToken}
            height="100%"
            refreshInterval={refreshInterval}
            onBucketSelect={onBucketSelect}
          />
        )}
      </div>
    </div>
  );
}

export default React.memo(TabSection);
